import { useEffect, useRef, useState, type FocusEvent } from "react";
import { Activity, Archive, Banknote, Building2, ConciergeBell, Database, Ellipsis, Eye, History, Hotel, KeyRound, LogOut, MessagesSquare, Target, UserCircle, PanelLeftClose, PanelLeftOpen, X, Users, Wand2, CheckCircle, ChevronRight, ChevronDown, ClipboardList, BarChart3, LayoutDashboard, LineChart, Network, Plug, Server, ToggleRight } from "lucide-react";
import { BillingStatusChip } from "./billing/BillingStatusChip";
import { WIZARD_STEPS, isWizardStepSkipped, wizardExpertMode, type WizardPathContext } from "./onboarding/wizardSteps";
import { groupRuns, strategySectionGroupLabels } from "./pricing/strategy-section-groups";
import { useExpertMode } from "../hooks/useExpertMode";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { ThemeToggle } from "./theme-toggle";
import { LanguageToggle } from "./language-toggle";
import { PmsConnectionSwitcher } from "./PmsConnectionSwitcher";
import { PmsSyncStatus, type SyncStatusDestination } from "./PmsSyncStatus";
import { useLanguage } from "../i18n/language-provider";
import { en } from "../i18n/en";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { handFocusBack, isHandingFocusBack } from "../navigation/focusHandBack";
import { AskOptivoProvider } from "./support/AskOptivoProvider";
import { AskOptivoRail } from "./support/AskOptivoRail";
import { brandLabelToString, type BrandLabel } from "../theme/brand";
import { BrandHeading } from "./brand/BrandHeading";
import type { UserRole } from "../types/config";
import { AppLink } from "./navigation/AppLink";
import type { PageName } from "../navigation/pages";
import { routePath, type AppRoute } from "../navigation/routes";
import { useFrontDeskAccess, useReadOnlyAccess, useStaffAccess, useViewerAccess } from "./access/read-only-access";
import { isFrontDeskPage } from "./access/front-desk-pages";
import { usePropertyOpenElsewhere } from "./pms/property-open-elsewhere";
import { RAIL_BESIDE_PAGE_QUERY, RailFrame, railColumnClass } from "./RailFrame";
import { RenderFaultBoundary } from "./RenderFaultBoundary";
import { PricesDestination } from "./rail/PricesDestination";
import { RailDestination } from "./rail/RailDestination";
import { FLYOUT_RAIL_GAP_PX, offsetPastRail } from "./rail/useRailFlyout";
import { CommandPaletteTrigger } from "./navigation/CommandPalette";
import { availableAccountSections } from "./account/account-sections";
import type { UserFeature } from "../services/userFeatures";

interface SidebarProps {
  currentPage: PageName;
  /** The route on screen, which marks a Prices tab or view; `{ page: currentPage }` where the shell names none. */
  currentRoute?: AppRoute;
  /** Leave for another screen; the shell decides whether it is open to this user. */
  onNavigate: (route: AppRoute) => void;
  /**
   * The shell's verdict on a screen for this user, so a link in an Ask Optivo
   * assistant's answer that it would refuse reads as text rather than as a
   * link that does nothing.
   */
  isPageAllowed?: (route: AppRoute) => boolean | "pending";
  onResetPassword?: () => void;
  onSignOut?: () => void;
  userRole?: UserRole;
  userEmail?: string | null;
  /**
   * What an administrator switched on for the person. Decides whether a
   * read-only viewer's account door leads anywhere: their tokens and AI
   * agents are behind it, and the payments are never theirs. None unless said.
   */
  userFeatures?: readonly UserFeature[];
  /**
   * Whether the caller's access to the open property reaches its whole
   * account. False for a member given some of its properties, whose account
   * door then leads only where a viewer's does. Undefined reads as before.
   */
  accountWholeAccount?: boolean;
  /** Whether sections that require completed analysis are locked */
  analysisRequired?: boolean;
  /** App-shell brand label shown in the header — reflects the active skin (e.g. Previo / "RMS by Optivo"). Falls back to the plain Optivo title. */
  brandLabel?: BrandLabel;
  /** Whether the sidebar is collapsed (icon-only) on desktop */
  collapsed?: boolean;
  /** Toggle collapsed state on desktop */
  onToggleCollapse?: () => void;
  /** Whether the mobile drawer is open */
  mobileOpen?: boolean;
  /** Close the mobile drawer */
  onMobileClose?: () => void;
  /** Active PMS connection ID */
  activeConnectionId?: number | null;
  /** Called when user switches active PMS connection */
  onConnectionChanged?: () => void;
  /** Increments whenever any connection is added/deleted/modified; triggers re-fetch in switcher */
  connectionVersion?: number;
  /** Current onboarding wizard step (only relevant when currentPage === "onboarding") */
  onboardingStep?: number;
  /** Called when user clicks a wizard step in the sidebar */
  onOnboardingStepChange?: (step: number) => void;
  /** Highest step the user has reached (steps up to this are clickable) */
  onboardingHighestStep?: number;
  /** Whether the user has completed the full onboarding wizard */
  onboardingCompleted?: boolean;
  /** Whether the wizard still has to ask for PMS credentials */
  pmsSetupRequired?: boolean;
  /**
   * Whether the run skips the question gates because the typology was picked
   * outright. Listing gates the wizard walks past numbers the rail against
   * screens the user never sees.
   */
  /** Whether competitor tracking is on, which is what the market gate needs. */
  compsetEnabled?: boolean;
  /** Sections the analysis did not measure; their steps are walked without expert mode. */
  unmeasuredSections?: WizardPathContext["unmeasuredSections"];
  /** Opens the quick-jump palette; the rail beside the page offers it only where the shell gives this. */
  onOpenCommandPalette?: () => void;
}

interface NavGroupItem {
  page: PageName;
  label: string;
  Icon: typeof Activity;
  /** Further pages the entry stands for: the views of one screen that each have an address. */
  alsoActiveOn?: readonly PageName[];
}

function itemIsCurrent(item: NavGroupItem, currentPage: PageName): boolean {
  return item.page === currentPage || (item.alsoActiveOn?.includes(currentPage) ?? false);
}

/**
 * The route behind each destination the sync indicator's popover names. The
 * indicator says where an action leads and the rail knows the address: the
 * Upload Log is a tab of the Prices page, not a page of its own. A table, so a
 * destination the indicator gains has no route until one is written here; the
 * ternary it replaces sent every destination but the Upload Log to the
 * connection screen.
 */
const SYNC_DESTINATION_ROUTES: Record<SyncStatusDestination, AppRoute> = {
  connections: { page: "connections" },
  uploadLog: { page: "drafts", tab: "uploadLog" },
  dashboard: { page: "dashboard" },
};

interface CollapsibleNavGroupProps {
  title: string;
  items: NavGroupItem[];
  /** Whether the whole rail is icon-only — the group then renders as bare icons */
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  currentPage: PageName;
  onSelect: (page: PageName) => void;
  /** Entries are disabled until the property analysis has completed */
  locked?: boolean;
}

/** A secondary nav section that folds away behind its own heading. */
function CollapsibleNavGroup({
  title,
  items,
  collapsed,
  open,
  onToggle,
  currentPage,
  onSelect,
  locked = false,
}: CollapsibleNavGroupProps) {
  if (collapsed) {
    return (
      <div className="space-y-1">
        {items.map((item) => (
          <RailDestination
            key={item.page}
            page={item.page}
            label={item.label}
            Icon={item.Icon}
            collapsed
            active={itemIsCurrent(item, currentPage)}
            locked={locked}
            onSelect={() => onSelect(item.page)}
          />
        ))}
      </div>
    );
  }

  // Nothing opens by itself; a closed group holding the page on screen says so in a stronger tone.
  const holdsCurrent = items.some((item) => itemIsCurrent(item, currentPage));
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`w-full flex items-center px-3 mb-1 py-1.5 min-h-10 sm:min-h-0 text-xs font-semibold uppercase tracking-wider hover:text-sidebar-foreground transition-colors ${!open && holdsCurrent ? "text-sidebar-foreground" : "text-sidebar-foreground/50"}`}
      >
        <span className="flex-1 text-left">{title}</span>
        <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-1">
          {items.map((item) => (
            <RailDestination
              key={item.page}
              page={item.page}
              label={item.label}
              Icon={item.Icon}
              collapsed={false}
              active={itemIsCurrent(item, currentPage)}
              locked={locked}
              onSelect={() => onSelect(item.page)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Nobody's features until said: the default for a rail drawn without them. */
const NO_FEATURES: readonly UserFeature[] = [];

/**
 * An icon button in the rail's own colours — what every footer control wears.
 *
 * 2.5rem below `sm`, where the root is 16px and an icon button's 2.25rem
 * comes to 36px: in the phone's menu the five stand side by side under a
 * thumb, and the rule there is 40. From `sm` up 2.25rem is 40.5px already, in
 * the menu and on the rail beside the page alike.
 */
const RAIL_ICON_CLASS = "size-10 sm:size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";
/** The same icon button on a page-coloured layer the rail opens. */
const PAGE_ICON_CLASS = "size-10 sm:size-9 text-popover-foreground hover:bg-accent hover:text-accent-foreground";

interface RailFooterControlsProps {
  /** Whether the connections page is the one on screen. */
  connectionsActive: boolean;
  onOpenConnections: () => void;
  onResetPassword?: () => void;
  onSignOut?: () => void;
  /** `page` where they stand on a popover over the page rather than on the rail. */
  tone?: "rail" | "page";
}

/**
 * The footer's controls, written once for both widths of the rail: the
 * expanded row and the collapsed column differ only in how they lay them out.
 *
 * Every one is an icon button in the rail's own colours. The appearance and
 * language switches default to the outline box the paper pages draw them in,
 * which on the rail is a light square among bare icons — so the rail asks for
 * `ghost`, and for the compact language switch, the one control that was
 * wider than an icon.
 */
function RailFooterControls({ connectionsActive, onOpenConnections, onResetPassword, onSignOut, tone = "rail" }: RailFooterControlsProps) {
  const { t } = useLanguage();
  const iconClass = tone === "page" ? PAGE_ICON_CLASS : RAIL_ICON_CLASS;

  return (
    <>
      <ThemeToggle variant="ghost" className={iconClass} />
      <LanguageToggle compact variant="ghost" className={iconClass} />
      <Button
        asChild
        variant="ghost"
        size="icon"
        className={`${iconClass} ${connectionsActive ? "bg-accent text-primary" : ""}`}
      >
        <AppLink
          to={routePath({ page: "connections" })}
          active={connectionsActive}
          onNavigate={onOpenConnections}
          title={t.connections.title}
          aria-label={t.connections.title}
        >
          <Plug className="h-4 w-4" />
        </AppLink>
      </Button>
      {onResetPassword && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onResetPassword}
          title={t.sidebar.resetPassword}
          aria-label={t.sidebar.resetPassword}
          className={iconClass}
        >
          <KeyRound className="h-4 w-4" />
        </Button>
      )}
      {onSignOut && (
        <Button variant="ghost" size="icon" onClick={onSignOut} title={t.sidebar.signOut} className={iconClass}>
          <LogOut className="h-4 w-4" />
        </Button>
      )}
    </>
  );
}

/** A focus the popover hands back explains nothing: the trigger's tooltip stays shut. */
function keepTooltipShutWhileHandingBack(event: FocusEvent<HTMLElement>) {
  if (isHandingFocusBack()) event.preventDefault();
}

/**
 * The folded rail's footer: the five controls in a popover behind one icon, since stacked they took most of the room
 * a laptop's folded nav has. The phone's menu is never folded and keeps the row.
 */
function FoldedFooterControls({
  connectionsActive,
  onOpenConnections,
  onResetPassword,
  onSignOut,
}: Omit<RailFooterControlsProps, "tone">) {
  const { t } = useLanguage();
  const label = t.sidebar.moreControls ?? en.sidebar.moreControls;
  const [open, setOpen] = useState(false);
  const [sideOffset, setSideOffset] = useState(FLYOUT_RAIL_GAP_PX);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Radix hands the focus back unless the dismissal came from outside; that rule is kept, and the hand-back marked.
  const interactedOutside = useRef(false);

  // The rail is hidden below md, not unmounted, and the portalled popover would be left at the screen's corner.
  const besidePage = useMediaQuery(RAIL_BESIDE_PAGE_QUERY);
  useEffect(() => {
    if (!besidePage) setOpen(false);
  }, [besidePage]);

  const close = () => setOpen(false);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          interactedOutside.current = false;
          if (triggerRef.current) setSideOffset(offsetPastRail(triggerRef.current, FLYOUT_RAIL_GAP_PX));
        }
        setOpen(next);
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild onFocus={keepTooltipShutWhileHandingBack}>
          <PopoverTrigger asChild>
            {/* The connections page is behind it, so it carries that page's chip; the link inside carries aria-current. */}
            <Button
              ref={triggerRef}
              variant="ghost"
              size="icon"
              aria-label={label}
              className={`${RAIL_ICON_CLASS} ${connectionsActive ? "bg-accent text-primary" : ""}`}
            >
              <Ellipsis className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={sideOffset}
        collisionPadding={8}
        aria-label={label}
        className="w-auto rounded-lg p-1"
        onInteractOutside={() => {
          interactedOutside.current = true;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const outside = interactedOutside.current;
          interactedOutside.current = false;
          const trigger = triggerRef.current;
          if (!outside && trigger) handFocusBack(() => trigger.focus());
        }}
      >
        <div data-testid="sidebar-more-controls-row" className="flex items-center gap-1">
          <RailFooterControls
            tone="page"
            connectionsActive={connectionsActive}
            onOpenConnections={() => {
              close();
              onOpenConnections();
            }}
            onResetPassword={onResetPassword && (() => {
              close();
              onResetPassword();
            })}
            onSignOut={onSignOut && (() => {
              close();
              onSignOut();
            })}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface ReadOnlyAccountBlockProps {
  collapsed: boolean;
  userEmail?: string | null;
  staff: boolean;
  /** The front desk: a viewer whose grant reads the prices and the reservations alone. */
  frontDesk?: boolean;
  /** The account settings are on screen. */
  active: boolean;
  /**
   * Opens the account settings; absent where they hold nothing for this
   * reader, and the block then leads nowhere.
   */
  onOpen?: () => void;
}

/**
 * The account door, for a read-only viewer: who is signed in, and that this
 * property may only be read. An administrator holding the same grant
 * (`staff`) gets the same block, naming what their access is instead, and so
 * does the front desk, whose grant reads the prices and the reservations and
 * none of the analytics the plain badge's hint promises.
 *
 * It opens their account settings (Oct 2026), which are theirs whatever
 * property is open: their security tokens and their AI-agent switch. It used
 * not to be a link, there being nothing of theirs behind it but billing for an
 * account they do not pay for — and the payments are still not offered them
 * there, nor is a billing status read for the badge's place. The tokens and
 * the AI agents are an administrator's to switch on, per person, so for
 * somebody they are off for the block is again what it was: no link, there
 * being nothing behind it (`onOpen` absent).
 *
 * The badge explains itself on focus, so it cannot sit inside the link: the
 * link is the e-mail, stretched over the whole row by a pseudo-element, and
 * the badge stands above that, outside it, where its tooltip still opens.
 */
function ReadOnlyAccountBlock({ collapsed, userEmail, staff, frontDesk = false, active, onOpen }: ReadOnlyAccountBlockProps) {
  const { t } = useLanguage();
  const ro = staff
    ? { badge: t.readOnlyAccess.staffBadge, badgeHint: t.readOnlyAccess.staffBadgeHint }
    : frontDesk
      ? { badge: t.readOnlyAccess.frontDeskBadge, badgeHint: t.readOnlyAccess.frontDeskBadgeHint }
      : t.readOnlyAccess;
  // The front desk's badge wears the mark the access panels give the front
  // desk; every other read-only access, the eye.
  const BadgeIcon = frontDesk && !staff ? ConciergeBell : Eye;
  const to = routePath({ page: "account" });

  if (collapsed) {
    return (
      <div className="py-3 border-b border-sidebar-border flex flex-col items-center gap-1.5">
        {onOpen ? (
          <AppLink
            to={to}
            active={active}
            onNavigate={onOpen}
            title={t.account.doorLabel}
            aria-label={t.account.doorLabel}
            className={`w-9 h-9 rounded-full bg-sidebar-accent flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-sidebar-primary ${active ? "ring-2 ring-sidebar-primary" : ""}`}
          >
            <UserCircle className="h-5 w-5 text-sidebar-foreground/70" aria-hidden="true" />
          </AppLink>
        ) : (
          <div className="w-9 h-9 rounded-full bg-sidebar-accent flex items-center justify-center">
            <UserCircle className="h-5 w-5 text-sidebar-foreground/70" aria-hidden="true" />
          </div>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <span role="img" aria-label={ro.badge} tabIndex={0} className="inline-flex text-sidebar-foreground/70">
              <BadgeIcon className="h-4 w-4" aria-hidden="true" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[min(16rem,var(--radix-tooltip-content-available-width))]">{ro.badgeHint}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="px-2 py-2 border-b border-sidebar-border">
      <div
        className={`relative w-full flex items-center gap-3 rounded-md px-2 py-1.5 ${onOpen ? "hover:bg-sidebar-accent" : ""} ${active ? "bg-sidebar-accent" : ""}`}
      >
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-sidebar-accent flex items-center justify-center">
          <UserCircle className="h-5 w-5 text-sidebar-foreground/70" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          {onOpen ? (
            <AppLink
              to={to}
              active={active}
              onNavigate={onOpen}
              title={t.account.doorLabel}
              aria-label={t.account.doorLabel}
              className="block min-h-4 text-xs text-sidebar-foreground/70 outline-none after:absolute after:inset-0 after:rounded-md focus-visible:after:ring-2 focus-visible:after:ring-sidebar-ring"
            >
              {userEmail && (
                <span className="block truncate" title={userEmail}>
                  {userEmail}
                </span>
              )}
            </AppLink>
          ) : (
            userEmail && (
              <p className="text-xs text-sidebar-foreground/70 truncate" title={userEmail}>
                {userEmail}
              </p>
            )
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="relative z-10 mt-0.5 inline-flex items-center gap-1 rounded-sm bg-sidebar-accent px-1.5 py-0.5 text-[11px] text-sidebar-foreground">
                <BadgeIcon className="h-3 w-3" aria-hidden="true" />
                {ro.badge}
              </span>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[min(16rem,var(--radix-tooltip-content-available-width))]">{ro.badgeHint}</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

interface PropertySwitcherUnavailableProps {
  collapsed: boolean;
  /** Whether the connections page is the one on screen. */
  active: boolean;
  onOpenConnections: () => void;
}

/**
 * What stands where the property switcher stood when it could not be drawn.
 *
 * The switcher does two things from a list it reads itself: it names the open
 * property and it switches to another. A list it cannot draw costs the name,
 * and nothing here can give that back, the name having come from the list. The
 * switch it can: the connections page lists every property and opens one, so
 * the link to it stands in the switcher's place, under the switcher's own
 * mark, and says why it is there on hover and on keyboard focus. It holds the
 * switcher's place exactly, open and folded, so nothing below it moves.
 *
 * It reads its words and nothing else, so it cannot fail the way the switcher
 * did.
 */
function PropertySwitcherUnavailable({ collapsed, active, onOpenConnections }: PropertySwitcherUnavailableProps) {
  const { t } = useLanguage();
  const why = t.connections.switcherUnavailable.replace("{page}", t.connections.title);
  const to = routePath({ page: "connections" });

  const link = collapsed ? (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className={`text-sidebar-foreground hover:bg-sidebar-accent ${active ? "bg-sidebar-accent" : ""}`}
    >
      <AppLink to={to} active={active} onNavigate={onOpenConnections} aria-label={t.connections.manage}>
        <Hotel className="h-4 w-4" />
      </AppLink>
    </Button>
  ) : (
    <AppLink
      to={to}
      active={active}
      onNavigate={onOpenConnections}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-sidebar-accent text-sidebar-foreground text-sm transition-colors ${active ? "bg-sidebar-accent" : ""}`}
    >
      <Hotel className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/70" />
      <span className="flex-1 text-left truncate">{t.connections.manage}</span>
    </AppLink>
  );

  return (
    <div className={collapsed ? "py-2 flex flex-col items-center border-b border-sidebar-border" : "px-3 py-2 border-b border-sidebar-border"}>
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="max-w-[min(16rem,var(--radix-tooltip-content-available-width))]">{why}</TooltipContent>
      </Tooltip>
    </div>
  );
}

export function Sidebar({
  currentPage,
  currentRoute,
  onNavigate,
  isPageAllowed,
  onResetPassword,
  onSignOut,
  userRole,
  userEmail,
  userFeatures = NO_FEATURES,
  accountWholeAccount,
  analysisRequired = false,
  brandLabel,
  collapsed: collapsedProp = false,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
  activeConnectionId = null,
  onConnectionChanged,
  connectionVersion = 0,
  onboardingStep = 0,
  onOnboardingStepChange,
  onboardingHighestStep = 0,
  onboardingCompleted: _onboardingCompleted = false,
  pmsSetupRequired = false,
  compsetEnabled = false,
  unmeasuredSections,
  onOpenCommandPalette,
}: SidebarProps) {
  const { t } = useLanguage();
  // A read-only viewer is a guest on somebody else's account: the rail offers
  // no strategy and no billing, and says on the account door what kind of
  // access this is. An administrator holding the same grant (`staff`) is
  // offered every page a member reads and no billing, and the badge says what
  // their access is. The door still opens their own account settings.
  const readOnly = useReadOnlyAccess();
  const viewer = useViewerAccess();
  const staff = useStaffAccess();
  // The front desk — a viewer whose grant reads the prices and the
  // reservations alone — is offered only the pages on its list
  // (`access/front-desk-pages.ts`), the one the shell judges by, so a page
  // added to a group later stays out of their rail until it is listed there.
  const frontDesk = useFrontDeskAccess();
  // Whether the account page holds a section for this reader, by the page's
  // own table: always the payments for somebody who runs the open property;
  // for somebody who only reads it, the tokens and the AI agents, which an
  // administrator switches on per person.
  const accountHasSection = availableAccountSections({
    readOnlyReason: readOnly ? (staff ? "staff" : "viewer") : null,
    features: userFeatures,
    wholeAccount: accountWholeAccount,
  }).length > 0;
  // The shell has found another property open than the one this tab — and so
  // the rail — was loaded for (`useOpenPropertyWatch`).
  const openElsewhere = usePropertyOpenElsewhere();
  // Not for a read-only viewer (the property's revenue manager is the one
  // they ask, and the server refuses them), nor before a property is open,
  // since a request is about one — nor while another property is open than
  // the one the rail names, whose request it would file and whose proposal
  // it would announce.
  const askEnabled = !readOnly && !openElsewhere && activeConnectionId != null;
  const label = brandLabel ?? { line1: t.sidebar.title, line2: null };
  // Marks a place inside a page; everything that reads again is keyed on the page alone.
  const route = currentRoute ?? ({ page: currentPage } as AppRoute);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  // Keep the original index — the wizard is driven by absolute step positions.
  // The rail lists the path the wizard actually walks, so it has to be filtered
  // with the same context the wizard navigates by.
  const [storedExpertMode] = useExpertMode();
  const expertMode = wizardExpertMode(storedExpertMode);
  const wizardGroupLabels = strategySectionGroupLabels(t);
  const visibleWizardSteps = WIZARD_STEPS
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => !isWizardStepSkipped(step.key, {
      pmsSetupRequired,
      compsetEnabled,
      expertMode,
      unmeasuredSections,
    }));

  const analyticsGroup: NavGroupItem[] = [
    { page: "performance", label: t.sidebar.performance, Icon: Activity },
    { page: "forecast", label: t.sidebar.forecast, Icon: BarChart3 },
    { page: "demandForecast", label: t.sidebar.demandForecast, Icon: LineChart },
    { page: "recentBookings", label: t.sidebar.recentBookings, Icon: ClipboardList },
    { page: "segmentChannel", label: t.sidebar.segmentChannel, Icon: Network },
    { page: "marketConditions", label: t.sidebar.marketConditions, Icon: Building2 },
    // What Optivo sent the property — a study states the strategy, which a
    // read-only viewer is not shown, and the server refuses them the archive.
    ...(viewer ? [] : [{ page: "studyArchive" as const, label: t.sidebar.studyArchive, Icon: Archive }]),
  ];
  const analyticsItems = analyticsGroup.filter((item) => !frontDesk || isFrontDeskPage(item.page));

  const adminItems: NavGroupItem[] = [
    { page: "users", label: t.sidebar.userManagement, Icon: Users },
    { page: "wirePaymentsAdmin", label: t.sidebar.wirePaymentsAdmin, Icon: Banknote },
    { page: "priceDraftsAdmin", label: t.sidebar.priceDraftsAdmin, Icon: Database },
    { page: "connectionsAdmin", label: t.sidebar.connectionsAdmin, Icon: Server },
    { page: "activityAdmin", label: t.sidebar.activityAdmin, Icon: History },
    // The cases, and what support promised to come back to on them: one
    // screen, whose reminders view has an address of its own.
    { page: "strategyHelpAdmin", label: t.sidebar.strategyHelpAdmin, Icon: MessagesSquare, alsoActiveOn: ["supportRemindersAdmin"] },
    // What is rolled out person by person, and the switch for each.
    { page: "featuresAdmin", label: t.sidebar.featuresAdmin, Icon: ToggleRight },
  ];

  const navigateTo = (route: AppRoute) => {
    onNavigate(route);
    // Close mobile drawer on navigation
    onMobileClose?.();
  };
  const handlePageChange = (page: PageName) => navigateTo({ page } as AppRoute);

  // The billing chip draws a status it reads itself, so it is drawn inside a
  // boundary: a status it cannot draw costs the chip and not the rail. Nothing
  // stands in for it. Nothing is what the chip shows whenever its status
  // cannot be read, and the account door around it still opens the account
  // settings, whose payments state the subscription in full. Tried again for another
  // property, which is when the chip reads again.
  const billingChip = (compact: boolean) => (
    <RenderFaultBoundary part="rail.billingStatus" retryKey={activeConnectionId} fallback={null}>
      <BillingStatusChip compact={compact} refreshKey={activeConnectionId} />
    </RenderFaultBoundary>
  );

  // A function of the rail's width rather than one element: the phone drawer
  // always opens at full width, whatever the desktop rail is folded to — a
  // strip of unlabelled icons is no way to find a page on a phone
  // (`RailFrame` asks for each).
  const renderContent = (collapsed: boolean) => (
    <div className={railColumnClass(collapsed)}>
      {/* Title + close/collapse: the rail's head. A zone of its own, which a
          skin may colour apart from everything below it — Previo's is its red
          bar over a grey rail (`skins.css`). It paints its own `bg-sidebar`
          to read the zone's tokens. */}
      <div data-rail-zone="head" className="bg-sidebar p-4 border-b border-sidebar-border flex items-center justify-between min-h-[57px]">
        {!collapsed && (
          <AppLink
            to={routePath({ page: "dashboard" })}
            onNavigate={() => handlePageChange("dashboard")}
            title={brandLabelToString(label)}
            className="min-w-0 flex-1 text-sidebar-foreground hover:opacity-80 transition-opacity"
          >
            <BrandHeading label={label} />
          </AppLink>
        )}
        {!collapsed && onOpenCommandPalette && (
          <CommandPaletteTrigger placement="header" onOpen={onOpenCommandPalette} />
        )}
        {/* Desktop fold toggle — named in the active language and saying
            which way it points: the rail folds itself on laptop screens, and
            the operator's choice, once made here, is remembered. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              aria-label={collapsed ? t.sidebar.expand : t.sidebar.collapse}
              aria-expanded={!collapsed}
              className="hidden md:inline-flex text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex-shrink-0"
            >
              {collapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{collapsed ? t.sidebar.expand : t.sidebar.collapse}</TooltipContent>
        </Tooltip>
        {/* Closes the phone's menu — named in the rail's own word for it,
            which what stands in for the rail reads too (`RailUnavailable`).

            Explained in a tooltip as the fold toggle is, though it is drawn
            below `md` only, because that is a width and not a device. A
            desktop window narrowed or zoomed past the breakpoint gets this
            same menu with a pointer and a keyboard — a 1366px laptop at 200%
            is 683px wide — and there the X says what it closes on hover and
            on keyboard focus. A finger is shown nothing: the tooltip ignores
            a touch pointer, and where a tap's focus opens it, the same tap's
            click closes it, with the menu, before a frame is drawn. Nor is it
            shown as the menu opens: the focus goes to the menu itself then,
            not to this, its first control (`RailFrame`).

            2.5rem below `sm`, where the root is 16px and 2.25rem comes to 36:
            a full touch target. The header is as tall as this control, so
            what stands in for the rail carries the same size. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onMobileClose}
              aria-label={t.sidebar.closeMenu}
              className="md:hidden size-10 sm:size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{t.sidebar.closeMenu}</TooltipContent>
        </Tooltip>
      </div>

      {/* User profile section — the account door: opens the account
          settings, and shows the subscription status where the credits count
          used to sit. For somebody who only reads the open property the door
          says what their access is instead, and reads no billing status. */}
      {readOnly ? (
        <ReadOnlyAccountBlock
          collapsed={collapsed}
          userEmail={userEmail}
          staff={staff}
          frontDesk={frontDesk}
          active={currentPage === "account"}
          onOpen={accountHasSection ? () => handlePageChange("account") : undefined}
        />
      ) : !collapsed ? (
        <div className="px-2 py-2 border-b border-sidebar-border">
          <AppLink
            to={routePath({ page: "account" })}
            active={currentPage === "account"}
            onNavigate={() => handlePageChange("account")}
            title={t.account.doorLabel}
            aria-label={t.account.doorLabel}
            className={`w-full flex items-center gap-3 rounded-md px-2 py-1.5 text-left cursor-pointer hover:bg-sidebar-accent ${currentPage === "account" ? "bg-sidebar-accent" : ""}`}
          >
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-sidebar-accent flex items-center justify-center">
              <UserCircle className="h-5 w-5 text-sidebar-foreground/70" />
            </div>
            <div className="flex-1 min-w-0">
              {userEmail && (
                <p className="text-xs text-sidebar-foreground/70 truncate" title={userEmail}>
                  {userEmail}
                </p>
              )}
              {billingChip(false)}
            </div>
          </AppLink>
        </div>
      ) : (
        <div className="py-3 border-b border-sidebar-border flex flex-col items-center gap-1.5">
          <AppLink
            to={routePath({ page: "account" })}
            active={currentPage === "account"}
            onNavigate={() => handlePageChange("account")}
            title={t.account.doorLabel}
            aria-label={t.account.doorLabel}
            className={`w-9 h-9 rounded-full bg-sidebar-accent flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-sidebar-primary ${currentPage === "account" ? "ring-2 ring-sidebar-primary" : ""}`}
          >
            <UserCircle className="h-5 w-5 text-sidebar-foreground/70" />
          </AppLink>
          {billingChip(true)}
        </div>
      )}

      {/* PMS Connection Switcher. It draws a list it reads itself, so it is
          drawn inside a boundary: a list it cannot draw costs the switcher,
          and the link to the page that lists the properties stands in. Tried
          again on what makes the switcher read its list again, the open
          property or a connection changing, which is also what opening a
          property from that page does. Until then a new try would be handed
          the same list. */}
      <RenderFaultBoundary
        part="rail.propertySwitcher"
        retryKey={`${activeConnectionId ?? "none"}:${connectionVersion}`}
        fallback={
          <PropertySwitcherUnavailable
            collapsed={collapsed}
            active={currentPage === "connections"}
            onOpenConnections={() => handlePageChange("connections")}
          />
        }
      >
        <PmsConnectionSwitcher
          activeConnectionId={activeConnectionId}
          onConnectionChanged={onConnectionChanged ?? (() => { })}
          onManageConnections={() => handlePageChange("connections")}
          collapsed={collapsed}
          connectionVersion={connectionVersion}
        />
      </RenderFaultBoundary>

      {/* Sync indicator — "everything is syncing and all is good, unless it
          isn't", under the property it is about. Silent until the property
          has an active strategy for the poller to run, and while another
          property is open than the one the rail names: its verdict would be
          that property's, under this one's name. */}
      {!openElsewhere && (
        <PmsSyncStatus
          refreshKey={`${activeConnectionId ?? "none"}:${connectionVersion ?? 0}`}
          connectionId={activeConnectionId}
          collapsed={collapsed}
          onNavigate={(destination) => navigateTo(SYNC_DESTINATION_ROUTES[destination])}
          dashboardOpen={currentPage === "dashboard"}
        />
      )}

      {/* `overflow-x-hidden` is not redundant: CSS computes a `visible` axis to
          `auto` when the other axis is not visible, so `overflow-y-auto` alone
          leaves the rail able to grow a horizontal scrollbar as well — under a
          focus ring, or a label a pixel past its `truncate`. The rail never
          pans sideways, so say so. */}
      <nav className="flex-1 p-2 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden touch-pan-y touch-pinch-zoom">
        <div className="space-y-1">
          {collapsed && onOpenCommandPalette && (
            <CommandPaletteTrigger placement="rail" onOpen={onOpenCommandPalette} />
          )}
          <RailDestination
            page="dashboard"
            label={t.sidebar.dashboard}
            Icon={LayoutDashboard}
            collapsed={collapsed}
            active={currentPage === "dashboard"}
            onSelect={() => handlePageChange("dashboard")}
          />
        </div>

        {!collapsed && (
          <p className="px-3 mt-4 mb-2 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
            {t.sidebar.sectionPricing}
          </p>
        )}
        <div className={`space-y-1 ${collapsed ? "mt-4" : ""}`}>
          {!viewer && (
            <RailDestination
              page="strategies"
              label={t.sidebar.strategies}
              Icon={Target}
              collapsed={collapsed}
              active={currentPage === "strategies"}
              locked={analysisRequired}
              onSelect={() => handlePageChange("strategies")}
            />
          )}
          <PricesDestination
            collapsed={collapsed}
            route={route}
            locked={analysisRequired}
            onboarding={currentPage === "onboarding"}
            navigateTo={navigateTo}
            onMobileClose={onMobileClose}
            connectionId={activeConnectionId}
            connectionVersion={connectionVersion}
            openElsewhere={openElsewhere}
          />
        </div>

        {/* Onboarding wizard steps — shown when on the onboarding page */}
        {currentPage === "onboarding" && !collapsed && (
          <div className="mt-4">
            <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
              {t.sidebar.setup}
            </p>
            {/* Under the five headings the strategy editor files the same
                ground under, so the wizard and the editor read as one model.
                The steps around the pricing ones (the PMS, the analysis, the
                review) stand outside them. */}
            {groupRuns(visibleWizardSteps, ({ step }) => ("group" in step ? step.group : undefined))
              .map((run, runIndex) => {
                const steps = (
                  <div className="space-y-0.5">
                    {run.items.map(({ step, index: i }) => {
                      const position = visibleWizardSteps.findIndex((entry) => entry.index === i);
                      const wizardT = t.wizard ?? {} as Record<string, string>;
                      const isCurrent = i === onboardingStep;
                      const isReachable = i <= onboardingHighestStep;
                      const isCompleted = i < onboardingHighestStep;
                      return (
                        <button
                          key={step.key}
                          onClick={() => isReachable && onOnboardingStepChange?.(i)}
                          disabled={!isReachable}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs rounded-md transition-colors ${isCurrent
                            ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                            : isReachable
                              ? "text-sidebar-foreground hover:bg-sidebar-accent cursor-pointer"
                              : "text-sidebar-foreground/40 cursor-default"
                            }`}
                        >
                          {isCompleted ? (
                            <CheckCircle className="h-3.5 w-3.5 text-success flex-shrink-0" />
                          ) : isCurrent ? (
                            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
                          ) : (
                            <span className="w-3.5 h-3.5 flex items-center justify-center text-[10px] flex-shrink-0">{position + 1}</span>
                          )}
                          <span className="truncate">{wizardT[step.labelKey] ?? step.labelKey}</span>
                        </button>
                      );
                    })}
                  </div>
                );
                if (!run.group) {
                  return (
                    <div key={`steps-${runIndex}`} className={runIndex > 0 ? "mt-2 border-t border-sidebar-border pt-2" : undefined}>
                      {steps}
                    </div>
                  );
                }
                const headingId = `wizard-steps-${run.group}`;
                return (
                  <div key={headingId} role="group" aria-labelledby={headingId} className="mt-2">
                    <p
                      id={headingId}
                      className="px-3 pb-1 text-[0.625rem] font-semibold uppercase tracking-wider text-sidebar-foreground/40"
                    >
                      {wizardGroupLabels[run.group]}
                    </p>
                    {steps}
                  </div>
                );
              })}
          </div>
        )}

        {currentPage === "onboarding" && collapsed && (
          <div className="mt-4 flex flex-col items-center">
            <Wand2 className="h-4 w-4 text-primary" />
          </div>
        )}

        {/* Secondary destinations — both collapsed by default, pinned to the
            bottom of the nav so the pricing workflow keeps the top of the rail. */}
        <div className="mt-auto pt-4 space-y-2">
          {/* A group holding nothing for this reader — the front desk's —
              is not drawn at all, rather than a heading over nothing. */}
          {analyticsItems.length > 0 && (
            <CollapsibleNavGroup
              title={t.sidebar.sectionAnalytics}
              items={analyticsItems}
              collapsed={collapsed}
              open={analyticsOpen}
              onToggle={() => setAnalyticsOpen((open) => !open)}
              currentPage={currentPage}
              onSelect={handlePageChange}
              locked={analysisRequired}
            />
          )}
          {/* Administration is a minor section only administrators ever need. */}
          {userRole === "admin" && (
            <CollapsibleNavGroup
              title={t.sidebar.administration}
              items={adminItems}
              collapsed={collapsed}
              open={adminOpen}
              onToggle={() => setAdminOpen((open) => !open)}
              currentPage={currentPage}
              onSelect={handlePageChange}
            />
          )}
        </div>
      </nav>

      {/* Ask Optivo — outside the scrolling nav so it stays in view on every
          page (see `askEnabled` for who is offered it). The window it opens
          belongs to the provider around both renders of the rail, so the
          phone's menu folds away from in front of it without taking it along. */}
      {askEnabled && (
        <AskOptivoRail
          collapsed={collapsed}
          onNavigate={navigateTo}
          onAsked={onMobileClose}
        />
      )}

      {/* Five controls, one icon button wide apiece: at 2.25rem each they take
          11.25rem of the 15rem row a 16rem rail leaves, and because every length
          here is rem that holds on every skin. Below `sm` an icon is 2.5rem, a
          full touch target at the phone's 16px root (`RAIL_ICON_CLASS`), and
          the five take 12.5rem of the same row.
          The labelled language switch was 1.7x an icon, and it is what pushed
          password and sign-out onto a second line whose icons lined up with
          nothing above them. The row is a grid of columns at least one icon
          wide, at either size of icon: `auto-fit` opens only as many as fit, so
          a control added past the sixth (the fifth below `sm`) starts a second
          line under the first column rather than painting past the rail's
          border — which the sign-out button once did — and centring each
          control in an equal column puts the first icon on the vertical the
          nav's icons share. Folded, the five wait behind one icon
          (`FoldedFooterControls`). */}
      <div className={`p-2 border-t border-sidebar-border ${collapsed ? "flex justify-center" : ""}`}>
        {collapsed ? (
          <FoldedFooterControls
            connectionsActive={currentPage === "connections"}
            onOpenConnections={() => handlePageChange("connections")}
            onResetPassword={onResetPassword}
            onSignOut={onSignOut}
          />
        ) : (
          <div
            data-testid="sidebar-footer-row"
            className="grid grid-cols-[repeat(auto-fit,minmax(2.5rem,1fr))] sm:grid-cols-[repeat(auto-fit,minmax(2.25rem,1fr))] justify-items-center gap-y-1"
          >
            <RailFooterControls
              connectionsActive={currentPage === "connections"}
              onOpenConnections={() => handlePageChange("connections")}
              onResetPassword={onResetPassword}
              onSignOut={onSignOut}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    // One "Ask Optivo" window for both renders of the rail: it floats over the
    // page and has to outlive the phone's menu, the rail folding and every
    // page change while a question is being written.
    <AskOptivoProvider
      enabled={askEnabled}
      refreshKey={`${activeConnectionId}:${connectionVersion}:${currentPage}`}
      // The live assistant's conversation is this property's, and a link in
      // one of its answers leaves through the rail's own move.
      connectionId={activeConnectionId}
      onNavigate={navigateTo}
      isPageAllowed={isPageAllowed}
    >
      {/* Beside the page, and in the phone's menu while it is open. The frame
          is shared with what stands in for the rail when it cannot be drawn
          (`RailUnavailable`), so that stands exactly where the rail did. */}
      <RailFrame collapsed={collapsedProp} mobileOpen={mobileOpen} onMobileClose={onMobileClose}>
        {renderContent}
      </RailFrame>
    </AskOptivoProvider>
  );
}
